import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Music, Users, Square, Info } from "lucide-react";

export const DancefloorCalculator = () => {
  const [guestCount, setGuestCount] = useState("100");
  const [dancingPercentage, setDancingPercentage] = useState("40");
  const [danceStyle, setDanceStyle] = useState("mixed");
  const [useImperial, setUseImperial] = useState(false);

  // Space per person based on dance style (in sqm)
  const spacePerPerson = {
    "packed": 0.4,      // Club-style, very tight
    "social": 0.6,      // Social dancing, some movement
    "mixed": 0.75,      // Mixed event, moderate space
    "formal": 1.0,      // Formal event, comfortable
    "performance": 1.5, // Performance/choreographed needs more space
  };

  const danceStyleLabels = {
    "packed": "Packed (Club-style)",
    "social": "Social Dancing",
    "mixed": "Mixed Event",
    "formal": "Formal/Wedding",
    "performance": "Performance/Choreographed",
  };

  // Calculate results
  const guests = parseInt(guestCount) || 0;
  const percentage = parseInt(dancingPercentage) || 0;
  const expectedDancers = Math.ceil(guests * (percentage / 100));
  const sqmPerPerson = spacePerPerson[danceStyle] || 0.75;
  const totalAreaSqm = expectedDancers * sqmPerPerson;
  
  // Calculate square dimensions (for a square dancefloor)
  const squareSide = Math.sqrt(totalAreaSqm);
  const roundedSquareSide = Math.ceil(squareSide * 2) / 2; // Round up to nearest 0.5m
  
  // Calculate rectangular options
  const rect4x3Width = Math.sqrt(totalAreaSqm * (4/3));
  const rect4x3Height = rect4x3Width * (3/4);
  
  // Convert to imperial
  const squareSideFt = roundedSquareSide * 3.28084;
  const roundedSquareSideFt = Math.ceil(squareSideFt * 2) / 2; // Round to nearest 0.5ft
  const totalAreaSqft = totalAreaSqm * 10.764;
  const rect4x3WidthFt = rect4x3Width * 3.28084;
  const rect4x3HeightFt = rect4x3Height * 3.28084;

  // Display values based on unit system
  const areaUnit = useImperial ? "sq ft" : "m²";
  const lengthUnit = useImperial ? "ft" : "m";
  const displayArea = useImperial ? totalAreaSqft.toFixed(0) : totalAreaSqm.toFixed(1);
  const displaySquareSide = useImperial ? roundedSquareSideFt.toFixed(1) : roundedSquareSide.toFixed(1);
  const displayRectWidth = useImperial ? (Math.ceil(rect4x3WidthFt * 2) / 2).toFixed(1) : (Math.ceil(rect4x3Width * 2) / 2).toFixed(1);
  const displayRectHeight = useImperial ? (Math.ceil(rect4x3HeightFt * 2) / 2).toFixed(1) : (Math.ceil(rect4x3Height * 2) / 2).toFixed(1);
  const sqPerPersonDisplay = useImperial ? (sqmPerPerson * 10.764).toFixed(1) + " sq ft" : sqmPerPerson + " m²";

  const getCapacityLevel = () => {
    if (percentage <= 25) return { label: "Light", color: "text-green-400", desc: "Plenty of space for dancers" };
    if (percentage <= 40) return { label: "Moderate", color: "text-cyan-400", desc: "Good balance of space" };
    if (percentage <= 55) return { label: "Active", color: "text-yellow-400", desc: "Popular dancefloor expected" };
    return { label: "High Energy", color: "text-orange-400", desc: "Maximize dancefloor space" };
  };

  const capacityLevel = getCapacityLevel();

  return (
    <Layout 
      title="Dancefloor Calculator" 
      subtitle="Calculate the ideal dancefloor size for your event"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Input Card */}
        <Card className="tactical-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Users className="h-5 w-5" />
              Event Details
            </CardTitle>
            {/* Unit Toggle */}
            <div className="flex items-center gap-3">
              <span className={`text-sm ${!useImperial ? 'text-foreground' : 'text-muted-foreground'}`}>Metric</span>
              <Switch
                checked={useImperial}
                onCheckedChange={setUseImperial}
                data-testid="unit-toggle"
              />
              <span className={`text-sm ${useImperial ? 'text-foreground' : 'text-muted-foreground'}`}>Imperial</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Guest Count */}
              <div className="space-y-2">
                <Label className="label-tactical">Total Guests</Label>
                <Input
                  type="number"
                  min="1"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  className="tactical-input text-2xl font-mono h-14"
                  data-testid="guest-count-input"
                />
              </div>

              {/* Dancing Percentage */}
              <div className="space-y-2">
                <Label className="label-tactical">Expected Dancing (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={dancingPercentage}
                  onChange={(e) => setDancingPercentage(e.target.value)}
                  className="tactical-input text-2xl font-mono h-14"
                  data-testid="dancing-percentage-input"
                />
                <p className="text-xs text-muted-foreground">
                  Typical: 30-50% for weddings, 50-70% for parties
                </p>
              </div>

              {/* Dance Style */}
              <div className="space-y-2">
                <Label className="label-tactical">Dance Style</Label>
                <Select value={danceStyle} onValueChange={setDanceStyle}>
                  <SelectTrigger className="tactical-input h-14" data-testid="dance-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(danceStyleLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {sqPerPersonDisplay} per person
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Square className="h-5 w-5" />
              Recommended Dancefloor Size
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-secondary/30 rounded-sm">
                <p className="label-tactical">Expected Dancers</p>
                <p className="text-3xl font-mono font-bold text-accent" data-testid="expected-dancers">
                  {expectedDancers}
                </p>
                <p className="text-xs text-muted-foreground">people</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm">
                <p className="label-tactical">Total Area</p>
                <p className="text-3xl font-mono font-bold text-cyan-400" data-testid="total-area">
                  {displayArea}
                </p>
                <p className="text-xs text-muted-foreground">{areaUnit}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm">
                <p className="label-tactical">Capacity</p>
                <p className={`text-2xl font-bold ${capacityLevel.color}`}>
                  {capacityLevel.label}
                </p>
                <p className="text-xs text-muted-foreground">{capacityLevel.desc}</p>
              </div>
            </div>

            {/* Recommended Dimensions */}
            <div className="space-y-4">
              <h3 className="label-tactical text-center">Recommended Dimensions</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Square Option */}
                <div className="p-4 bg-secondary/40 border border-border rounded-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Square className="h-4 w-4" />
                    <span className="font-semibold">Square Dancefloor</span>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-mono font-bold text-foreground" data-testid="square-dimensions">
                      {displaySquareSide}{lengthUnit} × {displaySquareSide}{lengthUnit}
                    </p>
                  </div>
                </div>

                {/* Rectangle Option */}
                <div className="p-4 bg-secondary/40 border border-border rounded-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Square className="h-4 w-4" />
                    <span className="font-semibold">Rectangle (4:3)</span>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-mono font-bold text-foreground" data-testid="rect-dimensions">
                      {displayRectWidth}{lengthUnit} × {displayRectHeight}{lengthUnit}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Preview */}
            <div className="flex justify-center">
              <div 
                className="border-2 border-accent bg-accent/10 flex items-center justify-center"
                style={{
                  width: `${Math.min(300, roundedSquareSide * 15)}px`,
                  height: `${Math.min(300, roundedSquareSide * 15)}px`,
                }}
              >
                <div className="text-center">
                  <Music className="h-8 w-8 text-accent mx-auto mb-2" />
                  <p className="text-sm font-mono text-accent">
                    {displaySquareSide}{lengthUnit} × {displaySquareSide}{lengthUnit}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {expectedDancers} dancers
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Info className="h-5 w-5" />
              Planning Guidelines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-3">
                <h4 className="font-semibold text-yellow-400">Space Per Person</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <span className="text-foreground">Packed ({useImperial ? "4.3 sq ft" : "0.4 m²"})</span> - Club-style, high energy</li>
                  <li>• <span className="text-foreground">Social ({useImperial ? "6.5 sq ft" : "0.6 m²"})</span> - Line dancing, simple moves</li>
                  <li>• <span className="text-foreground">Mixed ({useImperial ? "8.1 sq ft" : "0.75 m²"})</span> - Standard events</li>
                  <li>• <span className="text-foreground">Formal ({useImperial ? "10.8 sq ft" : "1.0 m²"})</span> - Weddings, elegant events</li>
                  <li>• <span className="text-foreground">Performance ({useImperial ? "16.1 sq ft" : "1.5 m²"})</span> - Choreographed dances</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-yellow-400">Typical Dancing Percentages</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <span className="text-foreground">Corporate Event</span> - 20-30%</li>
                  <li>• <span className="text-foreground">Wedding Reception</span> - 30-50%</li>
                  <li>• <span className="text-foreground">Birthday Party</span> - 40-60%</li>
                  <li>• <span className="text-foreground">Nightclub/Party</span> - 50-70%</li>
                  <li>• <span className="text-foreground">Dance Event</span> - 70-90%</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Reference */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Quick Reference ({useImperial ? "Imperial" : "Metric"})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground">Guests</th>
                    <th className="text-center py-2 text-muted-foreground">30% Dancing</th>
                    <th className="text-center py-2 text-muted-foreground">40% Dancing</th>
                    <th className="text-center py-2 text-muted-foreground">50% Dancing</th>
                  </tr>
                </thead>
                <tbody>
                  {[50, 100, 150, 200, 300].map(g => {
                    const calc = (pct) => {
                      const dancers = Math.ceil(g * (pct / 100));
                      const area = dancers * 0.75;
                      const sideM = Math.ceil(Math.sqrt(area) * 2) / 2;
                      const sideFt = Math.ceil(sideM * 3.28084 * 2) / 2;
                      return useImperial 
                        ? `${sideFt}ft × ${sideFt}ft`
                        : `${sideM}m × ${sideM}m`;
                    };
                    return (
                      <tr key={g} className="border-b border-border/50">
                        <td className="py-2 font-mono">{g}</td>
                        <td className="py-2 text-center font-mono text-cyan-400">{calc(30)}</td>
                        <td className="py-2 text-center font-mono text-cyan-400">{calc(40)}</td>
                        <td className="py-2 text-center font-mono text-cyan-400">{calc(50)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Based on mixed event spacing ({useImperial ? "8.1 sq ft" : "0.75 m²"} per person)
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DancefloorCalculator;
